import React, { useState, useEffect } from "react";
import { ToastContainer } from "react-toastify";
import ProfileTabs from "./ProfileTabs";
import ProfileTabContent from "./ProfileTabContent";
import LoadingScreen from "../../components/LoadingScreen";
import { useProfile } from "../../hooks/useProfile";
import { useTheme } from "../../hooks/useTheme";
import EditBusiness from "./user-profile/EditBusiness";
import EditUserProfile from "./user-profile/EditUserProfile";
import { Theme } from "./types/profile";
import { colorOptions, fontOptions } from "./theme/themeConstants";
import { PageTitle } from "src/shared/PageTitle";

const Profile: React.FC = () => {
  const [activeTab, setActiveTab] = useState("Business Profile");
  const [editBusiness, setEditBusiness] = useState(false);
  const [editUser, setEditUser] = useState(false);
  const [businessType, setBusinessType] = useState<string>("");
  const { profileData, loading, refetch } = useProfile();
  const { theme, loading: themeLoading, updateTheme } = useTheme();

  useEffect(() => {
    document.title = "Profile › iMetanic";
    const storedBusinessType = sessionStorage.getItem("business_type");
    setBusinessType(storedBusinessType || "");
  }, []);

  const handleEditBusiness = () => setEditBusiness(true);
  const handleCloseEditBusiness = () => setEditBusiness(false);

  const handleEditUser = () => setEditUser(true);
  const handleCloseEditUser = () => setEditUser(false);

  const handleUpdateTheme = async (newTheme: Theme) => {
    return await updateTheme(newTheme);
  };

  const handleResetTheme = async () => {
    const defaultTheme: Theme = {
      background_color: colorOptions["DodgerBlue"],
      font_color: "white",
      font_family: fontOptions["Sans-serif"],
      businessName: sessionStorage.getItem("business_type") || "iMetanic",
    };
    return await updateTheme(defaultTheme);
  };

  if (loading || themeLoading) {
    return <LoadingScreen />;
  }


  const fontClass = theme?.font_family ? fontOptions[theme.font_family] : "sans-serif";

  return (
    <div className={`bg-gray-100 w-full h-full flex flex-col ${fontClass}`}>
      <PageTitle title="- Profile" />
      <ToastContainer />
      <div className='flex flex-1 '>
        <div className='flex-1 p-5 overflow-auto'>
          <div className='mb-6 pb-4 border-b border-gray-300'>
            <h1 className='text-2xl font-semibold text-gray-800'>{activeTab}</h1>
          </div>
          <div className='flex flex-col lg:flex-row gap-6'>
            <div className='w-full lg:w-64 flex-shrink-0'>
              <ProfileTabs activeTab={activeTab} onTabChange={setActiveTab} theme={theme} />
            </div>
            <div className='flex-1 bg-white rounded-lg shadow-sm p-6'>
              <ProfileTabContent
                activeTab={activeTab}
                profileData={profileData}
                loading={loading}
                theme={theme} // ✅ use directly from useTheme
                businessType={businessType}
                onEditBusiness={handleEditBusiness}
                onEditUser={handleEditUser}
                onUpdateTheme={handleUpdateTheme}
                onResetTheme={handleResetTheme}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modals */}
      {editBusiness && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto'>
            <EditBusiness
              profileData={profileData}
              onClose={handleCloseEditBusiness}
              onSave={() => {
                refetch();
                handleCloseEditBusiness();
              }}
            />
          </div>
        </div>
      )}
      {editUser && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto'>
            <EditUserProfile
              profileData={profileData}
              onClose={handleCloseEditUser}
              onSave={() => {
                refetch();
                handleCloseEditBusiness();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
